import React, { useMemo, useState } from "react";
import {
  Box,
  Heading,
  Spinner,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  Button,
  useToast,
  Badge,
  VStack,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useResumeDetails, useResumeVersions } from "../hooks/useResumeDetails";
import ResumeVersionTimeline from "../components/ResumeVersionTimeline";
import resumeRepositoryService from "../services/resumeRepositoryService";
import {
  getCandidateDisplayName,
  getResumeSkills,
} from "../utils/resumeHelpers";

const PAGE_MARKER_REGEX = /^--\s*\d+\s*of\s*\d+\s*--$/i;

const SECTION_KEYWORDS = new Set([
  "career objective",
  "professional summary",
  "career contour",
  "organizational experience",
  "organisational experience",
  "work experience",
  "skills",
  "core competencies",
  "education",
  "certifications",
  "projects",
]);

const toSectionTitle = (value = "") =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const isLikelySectionHeading = (line = "") => {
  const cleaned = String(line || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return false;
  if (cleaned.length < 3 || cleaned.length > 48) return false;
  if (cleaned.includes("@") || /https?:\/\//i.test(cleaned)) return false;
  if (/^role\s*[-:]/i.test(cleaned)) return false;

  const lowered = cleaned.toLowerCase();
  if (SECTION_KEYWORDS.has(lowered)) return true;

  const letters = cleaned.match(/[A-Za-z]/g) || [];
  const upper = cleaned.match(/[A-Z]/g) || [];
  const uppercaseRatio = letters.length ? upper.length / letters.length : 0;
  const wordCount = cleaned.split(/\s+/).length;

  return uppercaseRatio > 0.78 && wordCount <= 5;
};

const parseExtractedSections = (lines = []) => {
  const sections = [];
  let current = { title: "Overview", rows: [] };

  const pushCurrent = () => {
    if (current.rows.length > 0) {
      sections.push(current);
    }
  };

  for (const rawLine of lines) {
    const normalizedLine = String(rawLine || "").replace(/\t/g, " ");
    const trimmed = normalizedLine.trim();

    if (PAGE_MARKER_REGEX.test(trimmed)) {
      continue;
    }

    if (isLikelySectionHeading(trimmed)) {
      pushCurrent();
      current = {
        title: toSectionTitle(trimmed),
        rows: [],
      };
      continue;
    }

    if (!trimmed) {
      const lastRow = current.rows[current.rows.length - 1];
      if (lastRow && lastRow.type !== "spacer") {
        current.rows.push({ type: "spacer", text: "" });
      }
      continue;
    }

    const isBullet = /^[•●▪◦\-*]/.test(trimmed);
    const cleanedText = trimmed.replace(/^[•●▪◦\-*]\s*/, "");
    current.rows.push({
      type: isBullet ? "bullet" : "text",
      text: cleanedText,
    });
  }

  pushCurrent();

  if (sections.length === 0) {
    return [
      {
        title: "Extract",
        rows: lines.map((line) => ({ type: "text", text: line })),
      },
    ];
  }

  return sections;
};

const ResumeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const { data, isLoading, isError } = useResumeDetails(id);
  const versionsQuery = useResumeVersions(id);
  const [isWrapped, setIsWrapped] = useState(true);

  const resume = useMemo(() => data?.data, [data]);
  const extractedText = useMemo(() => resume?.extractedText || "", [resume]);
  const extractedLines = useMemo(
    () => extractedText.split(/\r?\n/),
    [extractedText],
  );
  const extractStats = useMemo(() => {
    const lines = extractedLines.length;
    const words = extractedText.trim()
      ? extractedText.trim().split(/\s+/).length
      : 0;
    return {
      chars: extractedText.length,
      words,
      lines,
    };
  }, [extractedText, extractedLines]);
  const extractedSections = useMemo(
    () => parseExtractedSections(extractedLines),
    [extractedLines],
  );

  const handleDownload = async () => {
    if (!resume?._id) return;
    try {
      const response = await resumeRepositoryService.downloadResume(resume._id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.setAttribute("download", resume.originalFileName || "resume");
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast({ title: "Download failed", status: "error", duration: 2500 });
    }
  };

  const handleCopyExtract = async () => {
    if (!extractedText) return;
    try {
      await navigator.clipboard.writeText(extractedText);
      toast({
        title: "Copied extracted text",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy extracted text.",
        status: "error",
        duration: 2500,
      });
    }
  };

  if (isLoading) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  if (isError || !resume) {
    return (
      <Box p={6}>
        <Text color="red.500">Unable to load resume details.</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">{getCandidateDisplayName(resume)}</Heading>
        <HStack>
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button colorScheme="blue" onClick={handleDownload}>
            Download Resume
          </Button>
        </HStack>
      </HStack>

      <Tabs variant="enclosed">
        <TabList>
          <Tab>Summary</Tab>
          <Tab>Raw Extract</Tab>
          <Tab>Versions</Tab>
          <Tab>Activity</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Text mb={2}>Experience: {resume.experienceYears || 0} years</Text>
            <Text mb={2}>Location: {resume.currentLocation || "-"}</Text>
            <Text mb={2}>Company: {resume.currentCompany || "-"}</Text>
            <Text>Skills: {getResumeSkills(resume).join(", ") || "-"}</Text>
          </TabPanel>

          <TabPanel>
            {extractedText ? (
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
              >
                <Box
                  px={4}
                  py={3}
                  bg="gray.50"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" flexWrap="wrap" spacing={3}>
                    <Text fontWeight="semibold" color="gray.700">
                      Extracted Resume Text
                    </Text>
                    <HStack spacing={2}>
                      <Badge colorScheme="teal" variant="subtle">
                        {extractedSections.length} sections
                      </Badge>
                      <Badge colorScheme="blue" variant="subtle">
                        {extractStats.lines} lines
                      </Badge>
                      <Badge colorScheme="green" variant="subtle">
                        {extractStats.words} words
                      </Badge>
                      <Badge colorScheme="purple" variant="subtle">
                        {extractStats.chars} chars
                      </Badge>
                    </HStack>
                  </HStack>

                  <Divider my={3} />

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyExtract}
                    >
                      Copy Text
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsWrapped((prev) => !prev)}
                    >
                      {isWrapped ? "Disable Wrap" : "Enable Wrap"}
                    </Button>
                  </HStack>
                </Box>

                <Tabs size="sm" variant="soft-rounded" colorScheme="blue" p={3}>
                  <TabList>
                    <Tab>Structured View</Tab>
                    <Tab>Line View</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel px={0} pb={0} pt={4}>
                      <Box maxH="62vh" overflow="auto" px={1}>
                        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                          {extractedSections.map((section, index) => (
                            <Box
                              key={`${section.title}-${index}`}
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                              overflow="hidden"
                              bg="white"
                            >
                              <Box
                                px={3}
                                py={2}
                                bg="gray.50"
                                borderBottom="1px solid"
                                borderColor="gray.200"
                              >
                                <HStack justify="space-between">
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.700"
                                  >
                                    {section.title}
                                  </Text>
                                  <Badge variant="subtle" colorScheme="gray">
                                    {
                                      section.rows.filter(
                                        (row) => row.type !== "spacer",
                                      ).length
                                    }
                                  </Badge>
                                </HStack>
                              </Box>

                              <VStack
                                align="stretch"
                                spacing={0}
                                divider={<Divider borderColor="gray.100" />}
                              >
                                {section.rows.map((row, rowIndex) => {
                                  if (row.type === "spacer") {
                                    return (
                                      <Box
                                        key={`${section.title}-spacer-${rowIndex}`}
                                        h={2}
                                        bg="gray.50"
                                      />
                                    );
                                  }

                                  return (
                                    <HStack
                                      key={`${section.title}-row-${rowIndex}`}
                                      align="start"
                                      spacing={2}
                                      px={3}
                                      py={2}
                                    >
                                      {row.type === "bullet" ? (
                                        <Text
                                          fontSize="sm"
                                          color="blue.500"
                                          lineHeight="1.55"
                                        >
                                          •
                                        </Text>
                                      ) : null}
                                      <Text
                                        fontSize="sm"
                                        color="gray.700"
                                        lineHeight="1.55"
                                        whiteSpace={
                                          isWrapped ? "pre-wrap" : "pre"
                                        }
                                        fontFamily={
                                          row.type === "bullet"
                                            ? "inherit"
                                            : "mono"
                                        }
                                      >
                                        {row.text}
                                      </Text>
                                    </HStack>
                                  );
                                })}
                              </VStack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>
                    </TabPanel>

                    <TabPanel px={0} pb={0} pt={4}>
                      <Box
                        maxH="62vh"
                        overflow="auto"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <VStack
                          align="stretch"
                          spacing={0}
                          divider={<Divider borderColor="gray.100" />}
                        >
                          {extractedLines.map((line, index) => (
                            <HStack
                              key={`line-${index}`}
                              align="start"
                              spacing={0}
                              px={0}
                              py={0}
                              bg={index % 2 === 0 ? "white" : "gray.50"}
                            >
                              <Box
                                w="56px"
                                flexShrink={0}
                                px={3}
                                py={2}
                                textAlign="right"
                                borderRight="1px solid"
                                borderColor="gray.200"
                                bg="gray.50"
                              >
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  fontFamily="mono"
                                >
                                  {index + 1}
                                </Text>
                              </Box>
                              <Box px={3} py={2} flex="1">
                                <Text
                                  fontSize="sm"
                                  lineHeight="1.55"
                                  color="gray.700"
                                  fontFamily="mono"
                                  whiteSpace={isWrapped ? "pre-wrap" : "pre"}
                                  tabSize={4}
                                >
                                  {line || " "}
                                </Text>
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            ) : (
              <Box
                p={8}
                border="1px dashed"
                borderColor="gray.300"
                borderRadius="lg"
                bg="gray.50"
                textAlign="center"
              >
                <Text fontWeight="semibold" color="gray.600" mb={1}>
                  No extracted text available
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Upload a readable PDF/DOCX resume to view parsed raw text
                  here.
                </Text>
              </Box>
            )}
          </TabPanel>

          <TabPanel>
            {versionsQuery.isLoading ? <Spinner /> : null}
            <ResumeVersionTimeline versions={versionsQuery.data?.data || []} />
          </TabPanel>

          <TabPanel>
            <Text color="gray.500">
              Activity feed integration will be wired in the next phase.
            </Text>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ResumeDetailPage;
